import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import "./InterviewQuestions.css";
import { useSearchParams } from "react-router-dom";
import { INTERVIEW_DATA } from "./INTERVIEW_DATA";

// ─── Data ────────────────────────────────────────────────────────────────────

const COMPANIES = ["All", ...new Set(INTERVIEW_DATA.map(d => d.company))];
const ROLES = ["All", ...new Set(INTERVIEW_DATA.map(d => d.role))];
const DIFFICULTIES = ["All", "Easy", "Medium", "Hard"];
const CATEGORIES = ["All", ...new Set(INTERVIEW_DATA.map(d => d.category))];

const DIFFICULTY_CONFIG = {
  Easy: { color: "#2d7a3a", bg: "#e8f5eb", dot: "#4caf6e" },
  Medium: { color: "#8a5f00", bg: "#fff8e1", dot: "#f5a623" },
  Hard: { color: "#b91c1c", bg: "#fef2f2", dot: "#ef4444" },
};

const COMPANY_COLORS = {
  Google: "#4285F4", Amazon: "#FF9900", Microsoft: "#00A4EF",
  Meta: "#1877F2", Apple: "#555555", Netflix: "#E50914",
  Flipkart: "#2874F0", Uber: "#000000",
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function InterviewQuestions() {
  const [searchParams] = useSearchParams();

  const [search, setSearch] = useState("");
  const [company, setCompany] = useState(() => {
    const c = searchParams.get("company");
    return COMPANIES.includes(c) ? c : "All";
  });
  const [role, setRole] = useState(() => {
    const r = searchParams.get("role");
    return ROLES.includes(r) ? r : "All";
  });
  const [difficulty, setDifficulty] = useState("All");
  const [category, setCategory] = useState("All");
  const [expandedId, setExpandedId] = useState(null);
  const [bookmarks, setBookmarks] = useState(() => {
    try { return JSON.parse(localStorage.getItem("iv_bookmarks") || "[]"); } catch { return []; }
  });
  const [solved, setSolved] = useState(() => {
    try { return JSON.parse(localStorage.getItem("iv_solved") || "[]"); } catch { return []; }
  });
  const [showBookmarksOnly, setShowBookmarksOnly] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [viewMode, setViewMode] = useState("grouped"); // grouped | flat
  const [sortBy, setSortBy] = useState("default"); // default | difficulty | company
  const searchRef = useRef(null);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem("iv_bookmarks", JSON.stringify(bookmarks));
  }, [bookmarks]);
  useEffect(() => {
    localStorage.setItem("iv_solved", JSON.stringify(solved));
  }, [solved]);

  // Keyboard shortcut: Cmd/Ctrl+K to focus search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape") {
        setExpandedId(null);
        searchRef.current?.blur();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const toggleBookmark = useCallback((id) => {
    setBookmarks(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
  }, []);

  const toggleSolved = useCallback((id) => {
    setSolved(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  }, []);

  const copyAnswer = useCallback((id, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, []);

  const clearFilters = () => {
    setSearch(""); setCompany("All"); setRole("All");
    setDifficulty("All"); setCategory("All"); setShowBookmarksOnly(false);
  };

  const hasFilters = search || company !== "All" || role !== "All" ||
    difficulty !== "All" || category !== "All" || showBookmarksOnly;

  const filtered = useMemo(() => {
    let data = INTERVIEW_DATA.filter(item => {
      if (showBookmarksOnly && !bookmarks.includes(item.id)) return false;
      if (company !== "All" && item.company !== company) return false;
      if (role !== "All" && item.role !== role) return false;
      if (difficulty !== "All" && item.difficulty !== difficulty) return false;
      if (category !== "All" && item.category !== category) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          item.company.toLowerCase().includes(q) ||
          item.role.toLowerCase().includes(q) ||
          item.question.toLowerCase().includes(q) ||
          item.category.toLowerCase().includes(q) ||
          item.tags.some(t => t.toLowerCase().includes(q))
        );
      }
      return true;
    });
    if (sortBy === "difficulty") {
      const order = { Easy: 0, Medium: 1, Hard: 2 };
      data = [...data].sort((a, b) => order[a.difficulty] - order[b.difficulty]);
    } else if (sortBy === "company") {
      data = [...data].sort((a, b) => a.company.localeCompare(b.company));
    }
    return data;
  }, [search, company, role, difficulty, category, showBookmarksOnly, bookmarks, sortBy]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(item => {
      if (!map[item.company]) map[item.company] = [];
      map[item.company].push(item);
    });
    return map;
  }, [filtered]);

  const stats = useMemo(() => ({
    total: INTERVIEW_DATA.length,
    solved: solved.length,
    bookmarked: bookmarks.length,
    companies: new Set(INTERVIEW_DATA.map(d => d.company)).size,
    solvedPercent: Math.round((solved.length / INTERVIEW_DATA.length) * 100),
  }), [solved, bookmarks]);

  return (
    <div className="iq-root">
      {/* ── Header ── */}
      <header className="iq-header">
        <div className="iq-header-inner">
          <div className="iq-brand">
            <div className="iq-logo-mark">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect width="22" height="22" rx="6" fill="white" fillOpacity="0.15"/>
                <path d="M5 7h12M5 11h8M5 15h5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="17" cy="15" r="3" fill="#7fa832"/>
              </svg>
            </div>
            <div className="iq-brand-text">
              <span className="iq-brand-name">Interview<span>Vault</span></span>
              <span className="iq-brand-tagline">Crack top company interviews</span>
            </div>
          </div>

          <div className="iq-header-stats">
            <div className="iq-stat-pill">
              <div className="iq-stat-pill-icon solved-icon">✓</div>
              <div>
                <div className="iq-stat-pill-num">{stats.solved}/{stats.total}</div>
                <div className="iq-stat-pill-label">Solved</div>
              </div>
            </div>
            <div className="iq-stat-pill">
              <div className="iq-stat-pill-icon bookmark-icon">♡</div>
              <div>
                <div className="iq-stat-pill-num">{stats.bookmarked}</div>
                <div className="iq-stat-pill-label">Saved</div>
              </div>
            </div>
            <div className="iq-progress-wrap">
              <div className="iq-progress-label">
                <span>Progress</span>
                <span className="iq-progress-pct">{stats.solvedPercent}%</span>
              </div>
              <div className="iq-progress-bar">
                <div className="iq-progress-fill" style={{ width: `${stats.solvedPercent}%` }} />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Controls ── */}
      <div className="iq-controls-wrap">
        <div className="iq-controls-inner">
          {/* Search */}
          <div className="iq-search-row">
            <div className="iq-search-wrap">
              <svg className="iq-search-ico" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                ref={searchRef}
                className="iq-search"
                type="text"
                placeholder="Search questions, companies, tags… (⌘K)"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="iq-search-x" onClick={() => setSearch("")} aria-label="Clear search">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M18 6 6 18M6 6l12 12"/>
                  </svg>
                </button>
              )}
            </div>

            <div className="iq-view-toggle">
              <button
                className={`iq-view-btn ${viewMode === "grouped" ? "active" : ""}`}
                onClick={() => setViewMode("grouped")}
                title="Group by company"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                </svg>
              </button>
              <button
                className={`iq-view-btn ${viewMode === "flat" ? "active" : ""}`}
                onClick={() => setViewMode("flat")}
                title="List view"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Filter Pills Row */}
          <div className="iq-filter-row">
            <div className="iq-filter-group">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              <select className="iq-select" value={company} onChange={e => setCompany(e.target.value)}>
                <option value="All">All Companies</option>
                {COMPANIES.slice(1).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>

            <div className="iq-filter-group">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              <select className="iq-select" value={role} onChange={e => setRole(e.target.value)}>
                <option value="All">All Roles</option>
                {ROLES.slice(1).map(r => <option key={r}>{r}</option>)}
              </select>
            </div>

            <div className="iq-filter-group">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
              <select className="iq-select" value={difficulty} onChange={e => setDifficulty(e.target.value)}>
                {DIFFICULTIES.map(d => <option key={d}>{d === "All" ? "All Levels" : d}</option>)}
              </select>
            </div>

            <div className="iq-filter-group">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M4 6h16M4 10h16M4 14h8"/>
              </svg>
              <select className="iq-select" value={category} onChange={e => setCategory(e.target.value)}>
                <option value="All">All Categories</option>
                {CATEGORIES.slice(1).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>

            <div className="iq-filter-group">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"/>
              </svg>
              <select className="iq-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="default">Default Order</option>
                <option value="difficulty">By Difficulty</option>
                <option value="company">By Company</option>
              </select>
            </div>

            <button
              className={`iq-bookmark-filter-btn ${showBookmarksOnly ? "active" : ""}`}
              onClick={() => setShowBookmarksOnly(p => !p)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill={showBookmarksOnly ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
              Saved
            </button>

            {hasFilters && (
              <button className="iq-clear-btn" onClick={clearFilters}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12"/>
                </svg>
                Clear All
              </button>
            )}
          </div>

          {/* Results + Active Chips */}
          <div className="iq-results-bar">
            <span className="iq-results-count">
              <strong>{filtered.length}</strong> question{filtered.length !== 1 ? "s" : ""}
              {hasFilters && " found"}
            </span>
            <div className="iq-chips">
              {company !== "All" && (
                <span className="iq-chip" style={{ "--chip-color": COMPANY_COLORS[company] || "#263012" }}>
                  {company}
                  <button onClick={() => setCompany("All")}>×</button>
                </span>
              )}
              {role !== "All" && (
                <span className="iq-chip">
                  {role}
                  <button onClick={() => setRole("All")}>×</button>
                </span>
              )}
              {difficulty !== "All" && (
                <span className="iq-chip" style={{ "--chip-color": DIFFICULTY_CONFIG[difficulty]?.color }}>
                  {difficulty}
                  <button onClick={() => setDifficulty("All")}>×</button>
                </span>
              )}
              {category !== "All" && (
                <span className="iq-chip">
                  {category}
                  <button onClick={() => setCategory("All")}>×</button>
                </span>
              )}
              {showBookmarksOnly && (
                <span className="iq-chip">
                  Bookmarked only
                  <button onClick={() => setShowBookmarksOnly(false)}>×</button>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main ── */}
      <main className="iq-main">
        {filtered.length === 0 ? (
          <div className="iq-empty">
            <div className="iq-empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#263012" strokeWidth="1.4" strokeLinecap="round">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
            </div>
            <h3>No questions match your filters</h3>
            <p>Try broadening your search or removing some filters</p>
            <button className="iq-empty-cta" onClick={clearFilters}>Reset all filters</button>
          </div>
        ) : viewMode === "grouped" ? (
          // ── Grouped View ──
          <div className="iq-company-list">
            {Object.entries(grouped).map(([comp, questions]) => (
              <section key={comp} className="iq-company-section">
                <div className="iq-company-header" style={{ "--company-color": COMPANY_COLORS[comp] || "#263012" }}>
                  <div className="iq-company-logo">
                    <span>{comp[0]}</span>
                  </div>
                  <div className="iq-company-meta">
                    <h2 className="iq-company-name">{comp}</h2>
                    <div className="iq-company-pills">
                      <span className="iq-company-count">{questions.length} Q&amp;A</span>
                      <span className="iq-company-solved">
                        {questions.filter(q => solved.includes(q.id)).length} solved
                      </span>
                    </div>
                  </div>
                  <div className="iq-company-mini-progress">
                    <div
                      className="iq-company-mini-fill"
                      style={{ width: `${Math.round((questions.filter(q => solved.includes(q.id)).length / questions.length) * 100)}%` }}
                    />
                  </div>
                </div>

                <div className="iq-cards">
                  {questions.map((item, idx) => (
                    <QuestionCard
                      key={item.id}
                      item={item}
                      idx={idx}
                      isExpanded={expandedId === item.id}
                      isBookmarked={bookmarks.includes(item.id)}
                      isSolved={solved.includes(item.id)}
                      isCopied={copiedId === item.id}
                      onToggle={() => setExpandedId(prev => prev === item.id ? null : item.id)}
                      onBookmark={() => toggleBookmark(item.id)}
                      onSolve={() => toggleSolved(item.id)}
                      onCopy={() => copyAnswer(item.id, item.answer)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          // ── Flat View ──
          <div className="iq-flat-list">
            {filtered.map((item, idx) => (
              <QuestionCard
                key={item.id}
                item={item}
                idx={idx}
                isExpanded={expandedId === item.id}
                isBookmarked={bookmarks.includes(item.id)}
                isSolved={solved.includes(item.id)}
                isCopied={copiedId === item.id}
                showCompany
                onToggle={() => setExpandedId(prev => prev === item.id ? null : item.id)}
                onBookmark={() => toggleBookmark(item.id)}
                onSolve={() => toggleSolved(item.id)}
                onCopy={() => copyAnswer(item.id, item.answer)}
              />
            ))}
          </div>
        )}
      </main>

      <footer className="iq-footer">
        <div className="iq-footer-inner">
          <span className="iq-footer-brand">InterviewVault</span>
          <span className="iq-footer-sep">·</span>
          <span>{stats.solved} of {stats.total} questions solved</span>
          <span className="iq-footer-sep">·</span>
          <span>Press <kbd>⌘K</kbd> to search</span>
        </div>
      </footer>
    </div>
  );
}

// ─── QuestionCard ─────────────────────────────────────────────────────────────
function QuestionCard({ item, idx, isExpanded, isBookmarked, isSolved, isCopied, showCompany, onToggle, onBookmark, onSolve, onCopy }) {
  const diff = DIFFICULTY_CONFIG[item.difficulty];

  return (
    <div
      className={`iq-card ${isExpanded ? "iq-card--open" : ""} ${isSolved ? "iq-card--solved" : ""}`}
      style={{ "--card-delay": `${idx * 0.04}s` }}
    >
      {isSolved && <div className="iq-solved-bar" />}

      <div className="iq-card-top" onClick={onToggle} role="button" tabIndex={0}
        onKeyDown={e => e.key === "Enter" && onToggle()}>
        <div className="iq-card-meta">
          <span
            className="iq-difficulty-badge"
            style={{ color: diff.color, background: diff.bg }}
          >
            <span className="iq-diff-dot" style={{ background: diff.dot }} />
            {item.difficulty}
          </span>
          {showCompany && (
            <span className="iq-company-badge" style={{ "--company-color": COMPANY_COLORS[item.company] || "#263012" }}>
              {item.company}
            </span>
          )}
          <span className="iq-category-badge">{item.category}</span>
          {isSolved && (
            <span className="iq-solved-badge">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <path d="M20 6 9 17l-5-5"/>
              </svg>
              Solved
            </span>
          )}
        </div>

        <div className="iq-card-body">
          <p className="iq-question">{item.question}</p>
          <div className="iq-card-right">
            <div className="iq-action-btns" onClick={e => e.stopPropagation()}>
              <button
                className={`iq-action-btn bookmark-btn ${isBookmarked ? "active" : ""}`}
                onClick={onBookmark}
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill={isBookmarked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                </svg>
              </button>
              <button
                className={`iq-action-btn solve-btn ${isSolved ? "active" : ""}`}
                onClick={onSolve}
                title={isSolved ? "Mark unsolved" : "Mark solved"}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M20 6 9 17l-5-5"/>
                </svg>
              </button>
            </div>
            <div className={`iq-chevron ${isExpanded ? "iq-chevron--up" : ""}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="iq-answer-section">
          <div className="iq-answer-header">
            <span className="iq-answer-label">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/>
              </svg>
              Model Answer
            </span>
            <div className="iq-answer-actions">
              <button className="iq-copy-btn" onClick={onCopy}>
                {isCopied ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                      <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
          <p className="iq-answer">{item.answer}</p>
          <div className="iq-tags">
            {item.tags.map(tag => (
              <span key={tag} className="iq-tag">#{tag}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}